import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";

/*
 * Общие кирпичи главной: аватар, индикатор присутствия, числа, кнопка-действие.
 * Шкала: радиусы 8 / 12 / 20, отступы кратны 4, числа всегда tabular.
 */
export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;
export const appear = css`
  animation: ${fadeUp} 0.2s ${(p) => p.theme.motion.ease} both;
`;

export const Num = styled.span`
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum", "cv11";
  letter-spacing: -0.02em;
`;

export const Label = styled.span`
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.02em;
`;

const AvatarBox = styled.span<{ $size: number; $leader?: boolean }>`
  position: relative;
  flex: 0 0 ${(p) => p.$size}px;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  display: grid;
  place-items: center;
  grid-template-columns: 100%;
  border-radius: ${(p) => (p.$size >= 40 ? 12 : 10)}px;
  background-color: ${(p) => p.theme.colors.surfaceHover};
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: ${(p) => Math.round(p.$size * 0.36)}px;
  font-weight: 650;
  letter-spacing: 0.02em;
  ${(p) =>
    p.$leader &&
    css`
      box-shadow: 0 0 0 2px var(--bg, ${p.theme.colors.surface}), 0 0 0 4px ${p.theme.colors.primary};
    `}
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
  }
`;
const Dot = styled.span<{ $status: string; $size: number }>`
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: ${(p) => (p.$size >= 48 ? 14 : 10)}px;
  height: ${(p) => (p.$size >= 48 ? 14 : 10)}px;
  border-radius: 50%;
  border: 3px solid var(--bg, ${(p) => p.theme.colors.surface});
  box-sizing: content-box;
  background: ${(p) =>
    p.$status === "online"
      ? p.theme.colors.success
      : p.$status === "in_game"
        ? p.theme.colors.primary
        : p.theme.colors.borderLight};
`;
/** Согласованная монограмма: первые два знака ника. */
export function monogram(name: string) {
  return (name || "?").slice(0, 2).toUpperCase();
}
export type Presence = "online" | "in_game" | "offline";

export function PlayerAvatar({
  name,
  src,
  size = 40,
  leader,
  presence,
  label,
}: {
  name: string;
  src?: string;
  size?: number;
  leader?: boolean;
  presence?: Presence;
  label?: string;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <AvatarBox $size={size} $leader={leader} aria-hidden="true">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        monogram(name)
      )}
      {presence && <Dot $status={presence} $size={size} title={label} />}
    </AvatarBox>
  );
}

/** Нейтральная компактная кнопка-иконка 36×36 (touch-зона увеличивается на coarse-указателях). */
export const IconAction = styled.button<{ $tone?: "danger" | "accent" }>`
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  color: ${(p) =>
    p.$tone === "danger"
      ? p.theme.colors.danger
      : p.$tone === "accent"
        ? p.theme.colors.primary
        : p.theme.colors.textSecondary};
  transition:
    background ${(p) => p.theme.motion.fast},
    color ${(p) => p.theme.motion.fast},
    transform ${(p) => p.theme.motion.fast};
  svg {
    width: 18px;
    height: 18px;
  }
  &:hover:not(:disabled) {
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => (p.$tone === "danger" ? p.theme.colors.danger : p.theme.colors.text)};
  }
  &:active:not(:disabled) {
    transform: scale(0.94);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  @media (pointer: coarse) {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
  }
`;

export const spin = keyframes`to { transform: rotate(360deg); }`;
export const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-right-color: transparent;
  animation: ${spin} 0.7s linear infinite;
  flex: 0 0 auto;
`;
